// @ts-ignore
import { computed, type Ref, type ComputedRef } from 'vue'

// Extended Task type with phase and intervenant information
interface ProjectTask {
  id?: string | number
  phaseId?: number
  intervenantId?: number
  amount?: number
  status?: string
}

// Extended Intervenant type
interface ProjectIntervenant {
  id?: string | number
  name?: string
}

/**
 * Composable for calculating statistics across multiple phases in a project
 * Provides helper functions for phase progress, budget, and intervenant management
 */
export function useProjectPhases(
  tasks: Ref<ProjectTask[]> | ComputedRef<ProjectTask[]>,
  intervenants: Ref<ProjectIntervenant[]> | ComputedRef<ProjectIntervenant[]>
) {
  
  /**
   * Get all tasks for a specific phase
   */
  const getPhaseTasks = (phaseId: number) => {
    return tasks.value.filter((t: ProjectTask) => t.phaseId === phaseId)
  }
  
  /**
   * Calculate progress percentage for a phase based on completed tasks
   */
  const getPhaseProgress = (phaseId: number): number => {
    const phaseTasks = getPhaseTasks(phaseId)
    if (phaseTasks.length === 0) return 0
    
    const completedTasks = phaseTasks.filter(
      (t: ProjectTask) => (t.status as any) === 'COMPLETED'
    ).length
    
    return Math.round((completedTasks / phaseTasks.length) * 100)
  }
  
  /**
   * Calculate total budget for a phase
   */
  const getPhaseBudget = (phaseId: number): number => {
    const phaseTasks = getPhaseTasks(phaseId)
    return phaseTasks.reduce((sum: number, task: ProjectTask) => {
      return sum + (task.amount || 0)
    }, 0)
  }
  
  /**
   * Get all intervenants assigned to tasks in a phase
   */
  const getPhaseIntervenants = (phaseId: number): ProjectIntervenant[] => {
    const phaseTasks = getPhaseTasks(phaseId)
    const intervenantIds = [...new Set(
      phaseTasks
        .map((t: ProjectTask) => t.intervenantId)
        .filter((id : number): id is number => id !== undefined && id !== null)
    )]
    
    return intervenants.value.filter((i: ProjectIntervenant) => 
      i.id && intervenantIds.includes(Number(i.id))
    )
  }
  
  /**
   * Get count of active intervenants (with at least one IN_PROGRESS task)
   */
  const getActiveIntervenantsCount = (): number => {
    const activeIds = new Set(
      tasks.value
        .filter((t: ProjectTask) => t.status === 'IN_PROGRESS')
        .map((t: ProjectTask) => t.intervenantId)
        .filter((id : number): id is number => id !== undefined && id !== null)
    )
    return activeIds.size
  }
  
  /**
   * Calculate total budget across all tasks
   */
  const getTotalBudget = (): number => {
    return tasks.value.reduce((sum: number, task: ProjectTask) => {
      return sum + (task.amount || 0)
    }, 0)
  }
  
  /**
   * Get all tasks assigned to a specific intervenant
   */
  const getTasksForIntervenant = (intervenantId: number): ProjectTask[] => {
    return tasks.value.filter((t: ProjectTask) => t.intervenantId === intervenantId)
  }
  
  /**
   * Calculate statistics for all phases
   */
  const phaseStatistics = computed(() => {
    const uniquePhaseIds = [...new Set<number>(
      tasks.value
        .map((t: ProjectTask) => t.phaseId)
        .filter((id : number): id is number => id !== undefined && id !== null)
    )]
    
    return uniquePhaseIds.map((phaseId: number) => ({
      phaseId,
      progress: getPhaseProgress(phaseId),
      budget: getPhaseBudget(phaseId),
      taskCount: getPhaseTasks(phaseId).length,
      intervenantCount: getPhaseIntervenants(phaseId).length
    }))
  })
  
  return {
    // Methods
    getPhaseTasks,
    getPhaseProgress,
    getPhaseBudget,
    getPhaseIntervenants,
    getActiveIntervenantsCount,
    getTotalBudget,
    getTasksForIntervenant,
    
    // Computed
    phaseStatistics
  }
}
